import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Server, Brain, Plug, KeyRound } from 'lucide-react';
import api from '@/services/api';

import ServiceCostsSection from './integrations/ServiceCostsSection';
import LLMProvidersSection from './integrations/LLMProvidersSection';
import GoogleOAuthSection from './integrations/GoogleOAuthSection';

const emptyServiceForm = {
  name: '', category: 'infrastructure', monthly_cost: 0, unit: 'month',
  notes: '', is_active: true,
};

const emptyLLMForm = {
  name: '', provider: 'openai', model_name: '', api_key: '',
  cost_per_1k_input_tokens: 0, cost_per_1k_output_tokens: 0,
  is_active: false, notes: '',
};

/**
 * Admin → Integrations & Cost Configuration tab.
 *
 * Orchestrator only — every visual chunk lives in a sibling under
 * ./integrations/. Split from a 584-line monolith in iter123 to reduce
 * complexity and re-render surface.
 */
export const AdminIntegrationsTab = () => {
  const [tab, setTab] = useState('services'); // services | llms | keys
  const [services, setServices] = useState([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [editingService, setEditingService] = useState(null);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);
  const [llms, setLlms] = useState([]);
  const [editingLLM, setEditingLLM] = useState(null);
  const [showLLMDialog, setShowLLMDialog] = useState(false);
  const [llmForm, setLLMForm] = useState(emptyLLMForm);
  const [googleOAuth, setGoogleOAuth] = useState(null);
  const [googleForm, setGoogleForm] = useState({ client_id: '', client_secret: '' });
  const [savingGoogle, setSavingGoogle] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, g] = await Promise.all([
        api.get('/admin/service-costs').catch(() => ({ data: { items: [], total_monthly_recurring: 0 } })),
        api.get('/admin/llm-providers').catch(() => ({ data: { items: [] } })),
        api.get('/admin/google-oauth').catch(() => ({ data: null })),
      ]);
      setServices(s.data.items || []);
      setTotalMonthly(s.data.total_monthly_recurring || 0);
      setLlms(l.data.items || []);
      setGoogleOAuth(g.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Service Cost handlers ─────────────────────────────────────────
  const openCreateService = () => {
    setEditingService(null);
    setServiceForm(emptyServiceForm);
    setShowServiceDialog(true);
  };
  const openEditService = (s) => {
    setEditingService(s);
    setServiceForm({
      name: s.name, category: s.category, monthly_cost: s.monthly_cost,
      unit: s.unit, notes: s.notes || '', is_active: s.is_active,
    });
    setShowServiceDialog(true);
  };
  const saveService = async () => {
    if (!serviceForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...serviceForm, monthly_cost: parseFloat(serviceForm.monthly_cost) || 0 };
      if (editingService) {
        await api.patch(`/admin/service-costs/${editingService.service_cost_id}`, payload);
        toast.success('Service cost updated');
      } else {
        await api.post('/admin/service-costs', payload);
        toast.success('Service cost added');
      }
      setShowServiceDialog(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };
  const deleteService = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/service-costs/${s.service_cost_id}`);
      toast.success('Deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── LLM handlers ──────────────────────────────────────────────────
  const openCreateLLM = () => {
    setEditingLLM(null);
    setLLMForm(emptyLLMForm);
    setShowLLMDialog(true);
  };
  const openEditLLM = (l) => {
    setEditingLLM(l);
    setLLMForm({
      name: l.name, provider: l.provider, model_name: l.model_name,
      api_key: '',
      cost_per_1k_input_tokens: l.cost_per_1k_input_tokens || 0,
      cost_per_1k_output_tokens: l.cost_per_1k_output_tokens || 0,
      is_active: l.is_active, notes: l.notes || '',
    });
    setShowLLMDialog(true);
  };
  const saveLLM = async () => {
    if (!llmForm.name.trim() || !llmForm.model_name.trim()) {
      toast.error('Name and model are required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...llmForm,
        cost_per_1k_input_tokens: parseFloat(llmForm.cost_per_1k_input_tokens) || 0,
        cost_per_1k_output_tokens: parseFloat(llmForm.cost_per_1k_output_tokens) || 0,
      };
      if (editingLLM) {
        if (!payload.api_key) delete payload.api_key;
        await api.patch(`/admin/llm-providers/${editingLLM.provider_id}`, payload);
        toast.success('LLM provider updated');
      } else {
        if (!payload.api_key) { toast.error('API key is required for new providers'); setSaving(false); return; }
        await api.post('/admin/llm-providers', payload);
        toast.success('LLM provider added');
      }
      setShowLLMDialog(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };
  const activateLLM = async (l) => {
    try {
      await api.post(`/admin/llm-providers/${l.provider_id}/activate`);
      toast.success(`${l.name} is now the active LLM`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
    }
  };
  const deleteLLM = async (l) => {
    if (!window.confirm(`Delete LLM "${l.name}"?`)) return;
    try {
      await api.delete(`/admin/llm-providers/${l.provider_id}`);
      toast.success('Deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── Google OAuth handlers ─────────────────────────────────────────
  const saveGoogle = async () => {
    if (!googleForm.client_id || !googleForm.client_secret) {
      toast.error('Both Client ID and Client Secret are required'); return;
    }
    setSavingGoogle(true);
    try {
      await api.post('/admin/google-oauth', googleForm);
      toast.success('Google OAuth saved');
      setGoogleForm({ client_id: '', client_secret: '' });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSavingGoogle(false); }
  };

  if (loading) return <div className="text-slate-500 py-10 text-center">Loading integrations…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Plug className="w-6 h-6 text-emerald-600" />
          Integrations & Cost Configuration
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Declare 3rd-party service costs, configure LLM providers, and manage integration keys.
          These directly feed the custom plan pricing calculator.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b">
        {[
          { id: 'services', label: 'Service Costs', icon: Server },
          { id: 'llms',     label: 'LLM Providers', icon: Brain },
          { id: 'keys',     label: 'Integration Keys', icon: KeyRound },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`intg-subtab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 -mb-px text-sm font-medium ${
                tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'services' && (
        <ServiceCostsSection
          services={services} totalMonthly={totalMonthly}
          showDialog={showServiceDialog} setShowDialog={setShowServiceDialog}
          form={serviceForm} setForm={setServiceForm}
          editing={editingService} saving={saving}
          onCreate={openCreateService} onEdit={openEditService}
          onSave={saveService} onDelete={deleteService}
        />
      )}

      {tab === 'llms' && (
        <LLMProvidersSection
          llms={llms}
          showDialog={showLLMDialog} setShowDialog={setShowLLMDialog}
          form={llmForm} setForm={setLLMForm}
          editing={editingLLM} saving={saving}
          onCreate={openCreateLLM} onEdit={openEditLLM}
          onActivate={activateLLM} onSave={saveLLM} onDelete={deleteLLM}
        />
      )}

      {tab === 'keys' && (
        <GoogleOAuthSection
          googleOAuth={googleOAuth}
          googleForm={googleForm} setGoogleForm={setGoogleForm}
          saving={savingGoogle} onSave={saveGoogle}
        />
      )}
    </div>
  );
};

export default AdminIntegrationsTab;
